import {
  useOutsideClick
} from "./2GLSLYXR.jsx";
import {
  cn
} from "./O62HZC57.jsx";

// src/Popover/Popover.tsx
import { createSignal, createEffect, createContext, useContext, splitProps, Show, onCleanup, on } from "solid-js";
import { Dynamic, Portal } from "solid-js/web";

// src/utils/floating.ts
var getMaxSpacePlacement = ({ top, right, bottom, left }, allowPlacements) => {
  bottom = window.innerHeight - bottom;
  right = window.innerWidth - right;
  const space = { top, right, bottom, left };
  allowPlacements = allowPlacements ?? Object.keys(space);
  return allowPlacements.reduce((a, b) => space[a] > space[b] ? a : b);
};
var computePosition = (reference, floating, { alignment, placement, allowPlacements } = {}) => {
  const referenceRect = reference.getBoundingClientRect();
  const floatingRect = floating.getBoundingClientRect();
  placement = placement ?? getMaxSpacePlacement(referenceRect, allowPlacements);
  const actualRect = {
    top: referenceRect.top + window.scrollY,
    bottom: referenceRect.bottom + window.scrollY,
    left: referenceRect.left + window.scrollX,
    right: referenceRect.right + window.scrollX,
    width: referenceRect.width,
    height: referenceRect.height
  };
  const position = { top: 0, left: 0, placement, alignment };
  if (placement === "top" || placement === "bottom") {
    if (placement === "top") {
      position.top = actualRect.top - floatingRect.height;
    } else {
      position.top = actualRect.bottom;
    }
    if (alignment === "start") {
      position.left = actualRect.left;
    } else if (alignment === "end") {
      position.left = actualRect.right - floatingRect.width;
    } else {
      position.left = actualRect.left + actualRect.width / 2 - floatingRect.width / 2;
    }
  } else if (placement === "left" || placement === "right") {
    if (alignment === "start") {
      position.top = actualRect.top + actualRect.height / 2 - floatingRect.height / 2;
    } else if (alignment === "end") {
      position.top = actualRect.top + actualRect.height / 2 - floatingRect.height / 2;
    } else {
      position.top = actualRect.top + actualRect.height / 2 - floatingRect.height / 2;
    }
    if (placement === "left") {
      position.left = actualRect.left - floatingRect.width;
    } else {
      position.left = actualRect.right;
    }
  }
  return position;
};
var observeMove = (reference, update) => {
  window.addEventListener("resize", update);
  window.addEventListener("scroll", update);
  let current = reference;
  while (current.parentElement) {
    current = current.parentElement;
    current.addEventListener("scroll", update);
  }
  return () => {
    window.removeEventListener("resize", update);
    window.removeEventListener("scroll", update);
    let current2 = reference;
    while (current2.parentElement) {
      current2 = current2.parentElement;
      current2.removeEventListener("scroll", update);
    }
  };
};
var observeSize = (reference, update) => {
  const observer = new ResizeObserver(update);
  if (Array.isArray(reference)) {
    reference.forEach((r) => observer.observe(r));
  } else {
    observer.observe(reference);
  }
  return () => observer.disconnect();
};

// src/Popover/Popover.tsx
var PopoverContext = createContext();
function isAsyncFunction(func) {
  return Object.prototype.toString.call(func) === "[object AsyncFunction]";
}
var usePopoverContext = () => {
  const context = useContext(PopoverContext);
  if (!context) throw new Error("usePopoverContext must be used within a Popover component");
  return context;
};
var isRenderFunction = (children) => {
  return typeof children === "function" && children.length > 0;
};
var Popover = (props) => {
  const [params, rest] = splitProps(props, ["defaultOpen", "open", "onOutsideClick", "children"]);
  const [open, setOpen] = createSignal(params.defaultOpen ?? false);
  const [container, setContainer] = createSignal();
  const [portal, setPortal] = createSignal();
  createEffect(() => params.open != null && setOpen(params.open));
  createEffect(on(open, (value) => props.onOpenChange?.(value), { defer: true }));
  useOutsideClick(portal, open, (e, start) => {
    const event = new MouseEvent("click", { bubbles: true, cancelable: true });
    Object.defineProperties(event, {
      target: { value: e.target, writable: false },
      currentTarget: { value: container(), writable: false },
      relatedTarget: { value: start, writable: false }
    });
    params.onOutsideClick?.(event);
    event.defaultPrevented || event.target !== event.relatedTarget || setOpen(false);
  });
  const id = Math.random().toString(36).slice(2);
  const context = { id, open, setOpen, container, portal, setPortal };
  const inner = () => {
    const child = params.children;
    return isRenderFunction(child) ? child(context) : child;
  };
  return <PopoverContext.Provider value={context}>
      <div ref={setContainer} {...rest} data-open={open() ? "" : void 0} aria-controls={id}>
        {inner()}
      </div>
    </PopoverContext.Provider>;
};
var PopoverTrigger = (props) => {
  const [params, rest] = splitProps(props, ["as", "onClick", "class"]);
  const { open, setOpen } = usePopoverContext();
  const handleClick = async (e) => {
    e.stopPropagation();
    if (props.disabled) return;
    if (isAsyncFunction(params.onClick)) {
      await params.onClick?.(e);
    } else {
      params.onClick?.(e);
    }
    e.defaultPrevented || setOpen((prev) => !prev);
  };
  return <Dynamic
    type="button"
    class={cn(open() ? "expanded" : "unexpanded", params.class)}
    role="button"
    {...rest}
    component={params.as ?? "button"}
    onClick={handleClick}
    aria-expanded={open()}
  />;
};
var PopoverContent = (props) => {
  const [params, rest] = splitProps(props, ["as", "align", "class"]);
  const { id, open, container, portal, setPortal } = usePopoverContext();
  createEffect(() => {
    if (!open()) return;
    const reference = container();
    const floating = portal();
    if (reference == null || floating == null) return;
    floating.id = id;
    floating.role = "dialog";
    floating.style.position = "fixed";
    floating.style.zIndex = "99";
    const isMobile = () => window.innerWidth <= 768;
    const isIOS = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      return /iphone|ipod|ipad/.test(userAgent);
    };
    const isKeyboardOpen = () => {
      if (!isMobile()) return false;
      if (window.visualViewport) {
        return window.visualViewport.height < window.innerHeight * 0.75;
      }
      return window.innerHeight < window.outerHeight * 0.75;
    };
    const update = () => {
      const alignment = params.align || (() => {
        const { left: left2, right } = reference.getBoundingClientRect();
        return left2 > window.innerWidth - right ? "end" : "start";
      })();
      const referenceRect = reference.getBoundingClientRect();
      const floatingRect = floating.getBoundingClientRect();
      const isInput = reference.querySelector('input, textarea, [contenteditable="true"]') !== null;
      const mobile = isMobile();
      const keyboardVisible = isKeyboardOpen();
      const viewport = {
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight,
        offsetTop: window.visualViewport?.offsetTop ?? 0,
        offsetLeft: window.visualViewport?.offsetLeft ?? 0
      };
      const position = computePosition(reference, floating, {
        alignment,
        allowPlacements: ["top", "bottom"]
      });
      let top = referenceRect.top + (position.placement === "bottom" ? referenceRect.height : -floatingRect.height);
      let left = referenceRect.left + (alignment === "end" ? referenceRect.width - floatingRect.width : 0);
      if (left < viewport.offsetLeft) {
        left = viewport.offsetLeft;
      } else if (left + floatingRect.width > viewport.offsetLeft + viewport.width) {
        left = viewport.offsetLeft + viewport.width - floatingRect.width;
      }
      if (mobile) {
        floating.style.position = keyboardVisible ? "absolute" : "fixed";
        if (keyboardVisible && isInput) {
          if (isIOS()) {
            top = referenceRect.bottom + window.pageYOffset;
          } else {
            top = referenceRect.bottom + viewport.offsetTop;
          }
        } else {
          if (referenceRect.top > viewport.height * 0.6) {
            top = referenceRect.top - floatingRect.height;
          } else {
            top = referenceRect.bottom;
          }
          if (top < viewport.offsetTop) {
            top = referenceRect.bottom;
          } else if (top + floatingRect.height > viewport.offsetTop + viewport.height) {
            top = referenceRect.top - floatingRect.height;
          }
        }
      } else {
        if (top < viewport.offsetTop) {
          top = referenceRect.bottom;
        } else if (top + floatingRect.height > viewport.offsetTop + viewport.height) {
          top = referenceRect.top - floatingRect.height;
        }
      }
      floating.style.top = `${top}px`;
      floating.style.left = `${left}px`;
      const align = position.alignment == null ? "center" : position.alignment === "start" ? "left" : "right";
      floating.style.setProperty("--transform-origin", `${align} ${position.placement === "top" ? "bottom" : "top"}`);
      floating.style.setProperty("--reference-width", `${referenceRect.width}px`);
    };
    let updateTimer;
    const debouncedUpdate = () => {
      clearTimeout(updateTimer);
      updateTimer = setTimeout(() => {
        requestAnimationFrame(update);
      }, 16);
    };
    const optimizedUpdate = () => requestAnimationFrame(update);
    update();
    window.addEventListener("scroll", debouncedUpdate, { passive: true });
    window.addEventListener("resize", optimizedUpdate);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", optimizedUpdate);
      window.visualViewport.addEventListener("scroll", debouncedUpdate);
    }
    const cleanupMove = observeMove(reference, optimizedUpdate);
    const cleanupSize = observeSize([reference, floating], optimizedUpdate);
    onCleanup(() => {
      clearTimeout(updateTimer);
      cleanupMove();
      cleanupSize();
      window.removeEventListener("scroll", debouncedUpdate);
      window.removeEventListener("resize", optimizedUpdate);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", optimizedUpdate);
        window.visualViewport.removeEventListener("scroll", debouncedUpdate);
      }
    });
  });
  return <Show when={open()}>
      <Portal ref={setPortal}>
        <Dynamic component={params.as ?? "div"} {...rest} class={cn("animate-zoom-in origin-[--transform-origin]", params.class)} />
      </Portal>
    </Show>;
};
var PopoverWrap = Object.assign(Popover, {
  Trigger: PopoverTrigger,
  Content: PopoverContent
});

export {
  PopoverWrap
};
